import { HttpClient, HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment.development';
import { JwtToken, LoginRequestBody, LoginResponseBody, RegisterRequestBody, UserInfo } from '../../models/types';
import { jwtDecode } from 'jwt-decode'; // Ensure this import is correct

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  private orgRoles: { [key: number]: string } = {
    1: 'weather',
    2: 'sport',
    3: 'fire',
    4: 'government',
    5: 'hunter',
    6: 'culture',
    7: 'religion'
  };

  private token: string | null = null;
  onUserLoggedIn: EventEmitter<any> = new EventEmitter<any>();
  onUserLoggedOut: EventEmitter<any> = new EventEmitter<any>();

  constructor(private http: HttpClient) { }

  SendRegisterInfo(registerData: RegisterRequestBody): Observable<any> {
    return this.http.post(environment.registerURL, registerData, { observe: "response" });
  }

  SendLoginInfo(loginData: LoginRequestBody): Observable<HttpResponse<LoginResponseBody>> {
    return this.http.post<LoginResponseBody>(environment.loginURL, loginData, { observe: "response" });
  }

  getAllOrganisations(): Observable<any> {
    return this.http.get<any>(environment.organisationURL, { observe: "response" });
  }

  sendSelectedOrganisations(selectedOrganisations: number[]): Observable<any> {
    return this.http.post(environment.organisationURL, selectedOrganisations, { observe: "response" });
  }


  SetToken(newToken: string) {
    this.token = newToken;
    localStorage.setItem("token", newToken);
  }

  GetToken(): string | null {
    if (this.token == null && localStorage.getItem("token") != null) {
      this.token = localStorage.getItem("token");
    }
    return this.token;
  }

  IsValidToken(): boolean {
    const token = this.GetToken();
    if (token) {
      try {
        const decoded = jwtDecode(token) as JwtToken;
        return Date.now() < decoded.exp! * 1000;
      } catch (error) {
        console.error("Invalid token:", error);
        return false;
      }
    }
    return false;
  }

  IsUserLoggedIn(): boolean {
    return this.GetToken() != null && this.IsValidToken();
  }

  IsAdminLoggedIn(): boolean {
    return this.IsUserLoggedIn() && this.IsValidAdminToken();
  }

  IsOrgAdminLoggedIn(role: string): boolean {
    return this.IsUserLoggedIn() && this.IsValidOrgAdminToken(role);
  }

  ClearToken() {
    localStorage.removeItem("token");
    this.token = null;
  }

  GetLoggedInUserInfo(): UserInfo | undefined {
    if (this.IsUserLoggedIn() && this.IsValidToken()) {
      const tokenData = jwtDecode(this.GetToken()!) as JwtToken;
      console.log(tokenData);
      return {
        userID: tokenData.userId,
        username: tokenData.username,
        email: tokenData.email,
      } as UserInfo;
    }
    return undefined;
  }

  IsValidAdminToken(): boolean {
    const token = this.GetToken();
    if (token) {
      try {
        const decoded = jwtDecode<JwtToken>(token);

        // Check if the token is expired
        const isExpired = Date.now() >= (decoded.exp! * 1000);
        if (isExpired) {
          return false; // Token is expired
        }

        // Check if the role is 'super_admin'
        return decoded.role === 'super_admin';
      } catch (error) {
        console.error('Error decoding token:', error);
        return false; // Token is invalid
      }
    }

    return false; // No token
  }

  IsValidOrgAdminToken(inputRole: string): boolean {
    const token = this.GetToken();
    if (token) {
      try {
        const decoded = jwtDecode<JwtToken>(token);
        // Check if the token is expired
        const isExpired = Date.now() >= (decoded.exp! * 1000);
        if (isExpired) {
          return false; // Token is expired
        }

        // Check if the role is 'org_admin'
        return decoded.role === inputRole;
      } catch (error) {
        console.error('Error decoding token:', error);
        return false; // Token is invalid
      }
    }

    return false; // No token
  }

  IsAnyOrgAdminLoggedIn(orgIDs: number[]): boolean {
    for (const orgID of orgIDs) {
      const roleForOrg = this.orgRoles[orgID];
      if (roleForOrg && this.IsValidOrgAdminToken(roleForOrg)) {
        return true; // Token matches one of the expected roles
      }
    }
    return false; // No matching role found
  }
  
}
